import {inject, Injectable} from '@angular/core';
import {CpmCUserApiService} from '../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-user.api-service';
import {BehaviorSubject, firstValueFrom, Observable, Subject} from 'rxjs';
import {GlobalsService} from './globals.service';
import {CpmCUserApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-user.api-model';
import {CpmCCustomerApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-customer.api-model';
import {CpmCCustomerApiService} from '../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-customer.api-service';
import {ActivatedRouteSnapshot, NavigationEnd} from '@angular/router';
import {ECpmCRouteTypeApiEnum} from '../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {CpmCOrganizationApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-organization.api-model';
import {CpmCOrganizationApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-organization.api-service';
import {CpmCLocationApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-location.api-model';
import {CpmCLocationApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-location.api-service';
import {CpmCProjectApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-project.api-model';
import {CpmCProjectApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-project.api-service';
import {BaseCpmCApiModel} from '../../../gen/cpm-c-api-ts/src/models/cpmc-base.api-model';
import {CpmCServiceRequestApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-service-request.api-model';
import {CpmCServiceRequestApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request.api-service';
import {CustomerUrlBuilderUtil} from '../utils/customer-url-builder.util';
import {CpmCTaskApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-task.api-model';
import {CpmCTaskApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-task.api-service';
import {
    CpmCSdkRbacUserAllowedRouteResourceEntryApiSchema,
    CpmCSdkRbacUserAllowedRouteResourcesApiSchema
} from '../../../gen/cpm-c-api-ts/src/schemas/cpmc-sdk-rbac.api-schemas';
import {CpmCUserApiUtils} from '../../../gen/cpm-c-api-ts/src/utils/c-global/cpmc-user.api-utils';

/**
 * Session service
 */
@Injectable({
    providedIn: 'root'
})
export class SessionService {
    private readonly globalsService: GlobalsService = inject(GlobalsService);
    private isInitialiazing = false;
    private isReady = false;
    private readonly isReadySubject$: Subject<boolean> = new Subject<boolean>();

    private readonly userSource = new BehaviorSubject<CpmCUserApiModel>(undefined!);
    private _user?: CpmCUserApiModel;
    private _userRbacSummary?: CpmCSdkRbacUserAllowedRouteResourcesApiSchema;

    private readonly customerSource = new BehaviorSubject<CpmCCustomerApiModel | undefined>(undefined);
    private _customer?: CpmCCustomerApiModel;

    // ==================

    private readonly organizationSource = new BehaviorSubject<CpmCOrganizationApiModel | undefined>(undefined);
    private _organization?: CpmCOrganizationApiModel;

    private readonly locationSource = new BehaviorSubject<CpmCLocationApiModel | undefined>(undefined);
    private _location?: CpmCLocationApiModel;

    // ==================

    private readonly projectSource = new BehaviorSubject<CpmCProjectApiModel | undefined>(undefined);
    private _project?: CpmCProjectApiModel;

    // ==================

    private readonly serviceRequestSource = new BehaviorSubject<CpmCServiceRequestApiModel | undefined>(undefined);
    private _serviceRequest?: CpmCServiceRequestApiModel;

    // ==================

    private taskSource = new BehaviorSubject<CpmCTaskApiModel | undefined>(undefined);
    private _task?: CpmCTaskApiModel;

    // ==================

    private isRouteContextReady = false;

    // ===============================================================
    // ===============================================================

    public isRouteContextReadySubject: Subject<boolean> = new Subject<boolean>();
    public lastRouteParams: Record<string, string> = {};

    public currentUser$: Observable<CpmCUserApiModel> = this.userSource.asObservable();
    public currentCustomer$: Observable<CpmCCustomerApiModel | undefined> = this.customerSource.asObservable();
    public currentOrganization$: Observable<CpmCOrganizationApiModel | undefined> =
        this.organizationSource.asObservable();
    public currentServiceRequest$: Observable<CpmCServiceRequestApiModel | undefined> =
        this.serviceRequestSource.asObservable();
    public currentTask$: Observable<CpmCTaskApiModel | undefined> = this.taskSource.asObservable();

    public routeParams: Record<string, string> = {};

    // ===============================================================
    // ===============================================================

    /**
     * Init, called at app start to fetch current user if logged in
     */
    public async init() {
        if (this.isInitialiazing || this.isReady) {
            return;
        }
        this.isInitialiazing = true;

        this.globalsService.router.events.subscribe(async (routerEvent) => {
            if (routerEvent instanceof NavigationEnd) {
                this.isRouteContextReady = false;

                let params: Record<string, string> = {};
                const stack: ActivatedRouteSnapshot[] = [this.globalsService.router.routerState.snapshot.root];
                while (stack.length > 0) {
                    const route = stack.pop()!;
                    params = {...params, ...route.params};
                    stack.push(...route.children);
                }
                this.lastRouteParams = params;

                await this.changeCustomer(params['customerUUID'] ?? undefined);

                await this.changeOrganization(params['organizationUUID'] ?? undefined);
                await this.changeLocation(params['locationUUID'] ?? undefined);

                await this.changeProject(params['projectUUID'] ?? undefined);

                await this.changeServiceRequest(params['serviceRequestUUID'] ?? undefined);

                await this.changeTask(params['taskUUID'] ?? undefined);

                this.routeParams = params;

                this.isRouteContextReady = true;
                this.isRouteContextReadySubject.next(true);
            }
        });

        try {
            this._user = await firstValueFrom(CpmCUserApiService.getSelf());
            this.userSource.next(this._user);

            this._userRbacSummary = await firstValueFrom(CpmCUserApiService.getSelfAllowedRouteResources());

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            this.globalsService.messageService.add({
                severity: 'error',
                detail: 'Error: ' + (err?.error?.message ?? err.toString())
            });

            // Log error in the console for debugging, do not remove
            console.log(err);
            this.globalsService.router.navigate([CustomerUrlBuilderUtil.notFound()]);
        }

        this.isReady = true;
        this.isInitialiazing = false;

        this.isReadySubject$.next(true);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get if the service is ready
     * @returns True if ready
     */
    public getIsReady(): boolean {
        return this.isReady;
    }
    /**
     * Get a promise for when the service is ready
     * @returns The promise
     */
    public getIsReadyPromise(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            if (this.isReady) {
                resolve(true);
                return;
            }

            this.isReadySubject$.asObservable().subscribe({
                next: (response: boolean) => {
                    resolve(response);
                },
                error: () => {
                    reject(false);
                }
            });
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get current user model
     * @returns The user or undefined
     */
    public getUser(): CpmCUserApiModel | undefined {
        return this._user;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get if the route context is ready
     * @returns True if ready
     */
    public getIsRouteContextReady(): boolean {
        return this.isRouteContextReady;
    }
    /**
     * Get a promise for when the route context is ready
     * @returns The promise
     */
    public getRouteContextIsReadyPromise(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            if (this.isRouteContextReady) {
                resolve(true);
                return;
            }

            this.isRouteContextReadySubject.asObservable().subscribe({
                next: (response: boolean) => {
                    resolve(response);
                },
                error: () => {
                    reject(false);
                }
            });
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Change current customer if different
     * @param customerUUID New customer UUID
     */
    private async changeCustomer(customerUUID?: string) {
        await this.changeContextModel<CpmCCustomerApiModel>({
            inUUID: customerUUID,
            modelGetter: () => this._customer,
            modelSetter: (x) => {
                this._customer = x;
            },
            getByUUIDService: CpmCCustomerApiService.getCustomerByUUID,
            behaviorSubject: this.customerSource,
            customerDependant: false,
            allowNotFound: false
        });
    }
    /**
     * Get current customer
     * @returns The customer or undefined
     */
    public getCustomer(): CpmCCustomerApiModel | undefined {
        return this._customer;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Change current organization if different
     * @param organizationUUID New entitorganizationy UUID
     */
    public async changeOrganization(organizationUUID?: string) {
        await this.changeContextModel<CpmCOrganizationApiModel>({
            inUUID: organizationUUID,
            modelGetter: () => this._organization,
            modelSetter: (x) => {
                this._organization = x;
            },
            getByUUIDService: CpmCOrganizationApiService.getOrganizationByUUID,
            behaviorSubject: this.organizationSource,
            customerDependant: true,
            allowNotFound: false
        });
    }
    /**
     * Get current organization
     * @returns The organization or undefined
     */
    public getOrganization(): CpmCOrganizationApiModel | undefined {
        return this._organization;
    }
    /**
     * Change current location if different
     * @param locationUUID New location UUID
     */
    private async changeLocation(locationUUID?: string) {
        await this.changeContextModel<CpmCLocationApiModel>({
            inUUID: locationUUID,
            modelGetter: () => this._location,
            modelSetter: (x) => {
                this._location = x;
            },
            getByUUIDService: CpmCLocationApiService.getLocationByUUID,
            behaviorSubject: this.locationSource,
            customerDependant: true,
            allowNotFound: true
        });
    }
    /**
     * Get current location
     * @returns The location or undefined
     */
    public getLocation(): CpmCLocationApiModel | undefined {
        return this._location;
    }
    /**
     * Set current location
     * @param newLocation New location
     */
    public setLocation(newLocation: CpmCLocationApiModel) {
        this._location = newLocation;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Change current project if different
     * @param projectUUID New project UUID
     */
    public async changeProject(projectUUID?: string) {
        await this.changeContextModel<CpmCProjectApiModel>({
            inUUID: projectUUID,
            modelGetter: () => this._project,
            modelSetter: (x) => {
                this._project = x;
            },
            getByUUIDService: CpmCProjectApiService.getProjectByUUID,
            behaviorSubject: this.projectSource,
            customerDependant: true,
            allowNotFound: false
        });
    }
    /**
     * Get current project
     * @returns The project or undefined
     */
    public getProject(): CpmCProjectApiModel | undefined {
        return this._project;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Change current service request if different
     * @param serviceRequestUUID New service request UUID
     */
    private async changeServiceRequest(serviceRequestUUID?: string) {
        await this.changeContextModel<CpmCServiceRequestApiModel>({
            inUUID: serviceRequestUUID,
            modelGetter: () => this._serviceRequest,
            modelSetter: (x) => {
                this._serviceRequest = x;
            },
            getByUUIDService: CpmCServiceRequestApiService.getServiceRequestByUUID,
            behaviorSubject: this.serviceRequestSource,
            customerDependant: true,
            allowNotFound: false
        });

        if (this._serviceRequest) {
            await this.changeProject(this._serviceRequest.project_uuid);
            await this.changeOrganization(this._serviceRequest.organization_uuid);
        }
    }
    /**
     * Get current service request
     * @returns The service request or undefined
     */
    public getServiceRequest(): CpmCServiceRequestApiModel | undefined {
        return this._serviceRequest;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Change current task if different
     * @param taskUUID New task UUID
     */
    private async changeTask(taskUUID?: string) {
        await this.changeContextModel<CpmCTaskApiModel>({
            inUUID: taskUUID,
            modelGetter: () => this._task,
            modelSetter: (x) => {
                this._task = x;
            },
            getByUUIDService: CpmCTaskApiService.getTaskByUUID,
            behaviorSubject: this.taskSource,
            customerDependant: true,
            allowNotFound: false
        });

        if (this._task) {
            // Will cascade organization/project
            await this.changeServiceRequest(this._task.service_request_uuid);
        }
    }
    /**
     * Get current task
     * @returns The task or undefined
     */
    public getTask(): CpmCServiceRequestApiModel | undefined {
        return this._serviceRequest;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Check if the current user if allowed for route resource
     * @param endpoint Current full endpoint of the resource
     * @param type Type of the endpoint
     * @returns True if authorized for the requested resource
     */
    public isAuthorizedForRouteResource(endpoint: string, type: ECpmCRouteTypeApiEnum) {
        if (!this._user || !this._userRbacSummary || !endpoint) {
            return false;
        }
        if (CpmCUserApiUtils.has_super_admin_privileges(this._user)) return true;

        if (
            this._userRbacSummary.anonymous_route_resources.some((x) => this.matchEndpointDefinition(x, endpoint, type))
        )
            return true;
        if (
            this._userRbacSummary.any_logged_in_route_resources.some((x) =>
                this.matchEndpointDefinition(x, endpoint, type)
            )
        )
            return true;

        if (this._userRbacSummary.global_route_resources.some((x) => this.matchEndpointDefinition(x, endpoint, type)))
            return true;

        const customerUUID = this.getCustomer()?.uuid;
        if (
            customerUUID &&
            customerUUID in this._userRbacSummary.multi_schema_tenant_route_resources &&
            this._userRbacSummary.multi_schema_tenant_route_resources[customerUUID].some((x) =>
                this.matchEndpointDefinition(x, endpoint, type)
            )
        )
            return true;

        return false;
    }
    private matchEndpointDefinition(
        endpoint_definition: CpmCSdkRbacUserAllowedRouteResourceEntryApiSchema,
        current_endpoint: string,
        claim: ECpmCRouteTypeApiEnum
    ): boolean {
        if (endpoint_definition.type != claim) return false;

        const matchResult = current_endpoint.match(endpoint_definition.endpoint);
        if (matchResult && current_endpoint.endsWith(matchResult[0])) return true;

        return false;
    }
    // ===============================================================
    // ===============================================================

    /**
     * Change a customer depende
     * @param options.inUUID Input UUID
     * @param options.modelGetter Model getter functor
     * @param options.modelSetter Model setter functor
     * @param options.getByUUIDService Get model from Api by UUID service functor
     * @param options.behaviorSubject Behavior subject to emit
     * @param options.customerDependant Is it customer dependent?
     * @param options.allowNotFound Should we allow not found from Api? else redirect to not found page
     */
    private async changeContextModel<t_Model>(options: {
        inUUID: string | undefined;
        modelGetter: () => t_Model | undefined;
        modelSetter: (newMode: t_Model | undefined) => void;
        getByUUIDService: (uuid: string) => Observable<t_Model>;
        behaviorSubject: BehaviorSubject<t_Model | undefined>;
        customerDependant: boolean;
        allowNotFound: boolean;
    }) {
        if ((options.customerDependant && !this.getCustomer()) || options.inUUID?.length == 0)
            options.inUUID = undefined;

        const currentModel = options.modelGetter();

        // Already have the project, but the UUID is undefined, remove the project
        if (currentModel && options.inUUID === undefined) {
            options.modelSetter(undefined);
            options.behaviorSubject.next(undefined);
            return;
        }

        // Already have the project, and the UUID is the same, no change
        if (
            currentModel &&
            options.inUUID !== undefined &&
            (currentModel as unknown as BaseCpmCApiModel).uuid == options.inUUID
        ) {
            return;
        }

        // Don't have an project, and the UUID is not undefined, fetch the project
        if (!currentModel && options.inUUID !== undefined) {
            try {
                const newModel = await firstValueFrom(options.getByUUIDService(options.inUUID));
                options.modelSetter(newModel);
                options.behaviorSubject.next(newModel);
            } catch {
                if (!options.allowNotFound) {
                    this.globalsService.router.navigate([CustomerUrlBuilderUtil.notFound()]);
                    return;
                }

                options.modelSetter(undefined);
                options.behaviorSubject.next(undefined);
            }
        }
    }
}
